<template>
  <div class="help-page">
    <section class="help-hero" :style="heroStyle">
      <div class="help-hero-content">
        <h2>Hello, how can we help?</h2>
        <b-form class="help-search-form" @submit.prevent>
          <b-input-group class="input-group-merge">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input v-model="search" placeholder="Search a question..." />
          </b-input-group>
        </b-form>
        <p>Common troubleshooting topics: account setup, ads approval, billing, and reports.</p>
      </div>
    </section>

    <section class="popular-articles">
      <h3>Popular Articles</h3>
      <b-row class="justify-content-center article-row">
        <b-col
          v-for="(item, index) in filteredArticles"
          :key="index"
          lg="3"
          md="4"
          sm="12"
          class="mb-2 px-md-75"
        >
          <b-card class="article-card text-center h-100">
            <div class="article-icon-wrap">
              <b-img v-if="item.image" :src="item.image" width="36" class="article-icon-img" />
              <feather-icon v-else :icon="item.icon" size="30" class="article-icon" />
            </div>
            <h4>{{ item.title }}</h4>
            <b-card-text>{{ item.description }}</b-card-text>
            <b-button variant="outline-primary" size="sm" @click="openArticle(item)">Read More</b-button>
          </b-card>
        </b-col>
      </b-row>
    </section>

    <section class="read-more-section">
      <div class="read-more-inner">
        <div>
          <h3>Read More Articles</h3>
          <p class="mb-0">Browse additional guides for admin workflows, moderation, and platform settings.</p>
        </div>
        <b-button variant="primary" :to="{ name: 'pages-faq' }">Browse FAQ</b-button>
      </div>
      <b-row class="mt-2">
        <b-col v-for="(item, index) in moreArticles" :key="`more-${index}`" md="4" class="mb-1">
          <div class="read-more-card">
            <feather-icon :icon="item.icon" size="20" class="read-more-icon" />
            <div>
              <strong>{{ item.title }}</strong>
              <p class="mb-0 text-muted">{{ item.description }}</p>
            </div>
          </div>
        </b-col>
      </b-row>
    </section>
  </div>
</template>

<script>
import {
  BButton,
  BForm,
  BCard,
  BCardText,
  BCol,
  BImg,
  BInputGroup,
  BInputGroupPrepend,
  BFormInput,
  BRow,
} from 'bootstrap-vue'

const helpHeroBg = require('@/assets/images/illustration/faq-illustrations.svg')

export default {
  components: {
    BButton,
    BForm,
    BCard,
    BCardText,
    BRow,
    BCol,
    BImg,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
  },
  data() {
    return {
      search: '',
      articleCards: [
        {
          image: require('@/assets/images/icons/rocket.svg'),
          title: 'Getting Started',
          description: "Whether you're new or you're a power user, this article will help you navigate the admin panel.",
        },
        {
          icon: 'PlayCircleIcon',
          title: 'First Steps',
          description: 'Are you a new admin? Learn how to review ads, manage users, and configure plans.',
        },
        {
          icon: 'UploadCloudIcon',
          title: 'Upload & Approve Ads',
          description: 'This article shows how to review pending ads, reject with reason, and publish approved content.',
        },
      ],
      moreArticles: [
        { icon: 'UsersIcon', title: 'User Management', description: 'Suspend, block, and review user activity.' },
        { icon: 'BriefcaseIcon', title: 'Advertisers', description: 'Manage companies, followers, and uploaded ads.' },
        { icon: 'BarChart2Icon', title: 'Reports', description: 'Track suspended accounts and reported content.' },
      ],
    }
  },
  computed: {
    heroStyle() {
      return {
        backgroundColor: '#041122',
        backgroundImage: `linear-gradient(rgba(4, 17, 34, 0.82), rgba(4, 17, 34, 0.82)), url(${helpHeroBg})`,
        backgroundSize: 'cover, contain',
        backgroundPosition: 'center, right center',
        backgroundRepeat: 'no-repeat, no-repeat',
      }
    },
    filteredArticles() {
      const term = this.search.trim().toLowerCase()
      if (!term) return this.articleCards
      return this.articleCards.filter(item =>
        `${item.title} ${item.description}`.toLowerCase().includes(term))
    },
  },
  methods: {
    openArticle(item) {
      this.$router.push({ name: 'pages-faq', query: { q: item.title } })
    },
  },
}
</script>

<style lang="scss" scoped>
.help-hero {
  border-radius: 8px;
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  margin-bottom: 2rem;
  color: #fff;
}

.help-hero-content {
  width: 100%;
  max-width: 540px;
  text-align: center;
}

.help-hero-content h2 {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.help-hero-content p {
  font-size: 0.95rem;
  opacity: 0.92;
  margin-top: 0.8rem;
}

.popular-articles h3,
.read-more-section h3 {
  text-align: center;
  font-size: 1.75rem;
  font-weight: 600;
  color: #4b465c;
  margin-bottom: 1.4rem;
}

.article-card {
  border: 1px solid #dbdade;
  border-radius: 8px;
  padding-top: 1rem;
  max-width: 320px;
  margin: 0 auto;
}

.article-icon-wrap {
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.article-icon {
  color: #4a7ff0;
}

.article-card h4 {
  font-size: 1.15rem;
  font-weight: 600;
}

.article-card .card-text {
  color: #6e6b7b;
  min-height: 52px;
}

.read-more-section {
  margin-top: 2rem;
  padding: 1.5rem;
  border-radius: 8px;
  background: #f8f8fb;
  border: 1px solid #ebe9f1;
}

.read-more-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.read-more-card {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 1rem;
  height: 100%;
}

.read-more-icon {
  color: #4a7ff0;
  flex-shrink: 0;
  margin-top: 0.15rem;
}
</style>
